'use client';

// Custom chart components for the GenUI dashboard library. The bundled
// `openuiLibrary` from @openuidev/react-ui defines its charts WITHOUT
// exposing the underlying React `theme` (palette) prop in OpenUI Lang —
// every chart falls back to the default palette and the model can't vary
// colors. These wrappers re-define each chart with `palette` as an optional
// last positional arg, then pass it through to the underlying React
// component as `theme`. Used by `genui-dashboard-library.ts`.
//
// Schema arg order matches the bundled chart for backwards compatibility:
// existing model-emitted code keeps working; the new `palette` arg is
// purely additive at the tail of the positional list.
import React, { useEffect, useState } from 'react';
import { defineComponent, useIsStreaming } from '@openuidev/react-lang';
import type { DefinedComponent } from '@openuidev/react-lang';
import { z } from 'zod/v4';

// ---- Recharts max-update-depth guard ----------------------------------------
//
// Recharts (used inside every BarChart/LineChart/AreaChart/etc. component
// from @openuidev/react-ui) hits "Maximum update depth exceeded" when it
// re-mounts repeatedly with shape-changing partial data during streaming.
// Each new token from the LLM bumps the parser, which bumps the prop tree,
// which bumps recharts, which schedules another setState in its internal
// resize/animation loop — eventually React kills the tree.
//
// Library-blessed fix: gate chart mounting on `useIsStreaming()` from
// @openuidev/react-lang. While streaming, render a lightweight placeholder
// at the same height so layout doesn't jump. Once the stream completes
// (`isStreaming === false`), mount the real recharts-backed chart with
// the FINAL data — recharts only sees one stable mount, no infinite loop.
function ChartStreamingPlaceholder({ height = 220, label }: { height?: number; label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ? `Drawing ${label}` : 'Drawing chart'}
      className="flex items-end gap-1 rounded-lg border border-violet-500/15 bg-violet-500/[0.04] p-3"
      style={{ height }}
    >
      <span className="sr-only">Drawing chart…</span>
      {[40, 70, 30, 85, 55, 60, 45, 75].map((h, i) => (
        <span
          key={i}
          className="flex-1 rounded-t-sm bg-gradient-to-t from-violet-500/40 to-violet-400/20 motion-safe:animate-pulse"
          style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

// Streaming gate wrapped around every chart.
//
// Earlier behaviors that failed:
// 1. Gate on the global useIsStreaming() flag — charts waited for the
//    ENTIRE response to finish, even ones whose data had landed seconds
//    earlier.
// 2. Snapshot children with useEffect([children]) debounce — `children`
//    is a new React element reference EVERY render, so the timer reset
//    on every parse step (which happens many times per second during
//    streaming). The 300ms timer never fired until streaming ended.
//
// Current behavior: take a string `dataKey` from the caller (a hash of
// THAT chart's own props content). useEffect deps on a string compare
// by VALUE — when the chart's own data is stable, the deps don't change
// even if React re-renders due to siblings updating. The 300ms timer
// runs to completion and the chart mounts.
//
// recharts still mounts once with stable props per chart, dodging the
// "Maximum update depth" infinite-resize loop the original gate guarded.
function ChartGate({
  children,
  height,
  label,
  dataKey,
}: {
  children: React.ReactNode;
  height?: number;
  label?: string;
  /** Stable string-hash of THIS chart's data content. */
  dataKey: string;
}) {
  const isStreaming = useIsStreaming();
  // `frozen` holds the rendered chart element. Once set during streaming
  // (first 300ms quiet), it does NOT update again until streaming ends —
  // any further prop churn while streaming would expose recharts to the
  // "Maximum update depth" infinite-resize loop the original gate
  // existed to prevent.
  const [frozen, setFrozen] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (!isStreaming) {
      // Stream finished — commit the final state immediately. Single
      // recharts re-render with final data; safe because streaming is
      // done, no further churn coming.
      setFrozen(children);
      return;
    }
    if (frozen !== null) {
      // Already mounted with stable data mid-stream. Skip further updates
      // to avoid the recharts resize storm. The effect still runs on
      // every parent re-render (because children/dataKey are in deps),
      // but it's a no-op until streaming ends.
      return;
    }
    // Mid-stream + not yet committed — wait for a 300ms quiet window on
    // THIS chart's data. Sibling re-renders don't reset the timer
    // because dataKey's string value compares unchanged then.
    const t = setTimeout(() => setFrozen(children), 300);
    return () => clearTimeout(t);
  }, [dataKey, isStreaming, frozen, children]);

  if (frozen === null) return <ChartStreamingPlaceholder height={height} label={label} />;
  return <>{frozen}</>;
}

/**
 * Build a stable string-hash of a chart's data props. Used to feed
 * ChartGate's `dataKey` so its debounce only resets when the chart's
 * OWN data content actually changes — not on every parent re-render.
 *
 * Use JSON.stringify of just the data-shaping props (labels + series).
 * Cheap because chart payloads are small (handful of arrays).
 */
function chartDataKey(labels: unknown, series: unknown): string {
  try {
    return JSON.stringify({ l: labels, s: series });
  } catch {
    return String(labels) + '::' + String(series);
  }
}

import {
  AreaChartCondensed as AreaChartCondensedComponent,
  BarChartCondensed as BarChartCondensedComponent,
  HorizontalBarChart as HorizontalBarChartComponent,
  LineChartCondensed as LineChartCondensedComponent,
  PieChart as PieChartComponent,
  RadarChart as RadarChartComponent,
  RadialChart as RadialChartComponent,
  ScatterChart as ScatterChartComponent,
  SingleStackedBar as SingleStackedBarComponent,
} from '@openuidev/react-ui';

// PaletteName mirrors the React-side type ("ocean" | "orchid" | "emerald" |
// "sunset" | "spectrum" | "vivid"). Keeping the union explicit instead of
// importing it so the schema enum is reachable at runtime for prompt
// generation.
const PaletteEnum = z
  .enum(['ocean', 'orchid', 'emerald', 'sunset', 'spectrum', 'vivid'])
  .describe(
    'Color palette family. Pick deliberately: ocean=cool blues (finance), emerald=greens (nature), sunset=warm oranges (energy), orchid=purples (creative), spectrum=multi-hue, vivid=high-contrast. Different charts in the same dashboard SHOULD use different palettes for visual variety.',
  );
type PaletteName = z.infer<typeof PaletteEnum>;

// The underlying React chart prop is `theme: PaletteName`. We rename to
// `palette` in our DSL because `theme` is overloaded — the React-Ui
// `ThemeProvider` also uses that name for surface tokens, and we want the
// LLM-facing arg to feel chart-specific.
type ChartArgs = { theme?: PaletteName; customPalette?: string[] };

// Permissive zod schema for the model-facing customPalette arg. Keeps
// validation light because a missing/wrong color isn't a runtime crash —
// the chart lib falls back to its default palette. We just need to accept
// the array and forward it.
const HexArrayArg = z.array(z.string()).optional();

// Single-hue palettes only have shades of one color and look mono-tonal.
// Fine for one series, terrible for two+ — both end up as nearly-identical
// purples (or blues, or greens), which makes the chart read as broken.
// The model is told this in the prompt but still picks `orchid` etc. for
// multi-series charts. Auto-fallback to `vivid` when seriesCount > 1 and
// the requested palette is single-hue, so we get distinguishable colors
// without forcing the model to be perfect.
const SINGLE_HUE_PALETTES = new Set<PaletteName>(['ocean', 'orchid', 'emerald']);

// Custom palette for any chart where the upstream `vivid` would otherwise
// give us harsh / unbalanced colors.
//
// Why this exists: the lib's `getDistributedColors(palette, n)` helper
// picks `[mid-1, mid+1]` for n=2 and expands outward from mid for n≥3.
// Upstream `vivid` is a saturated rainbow whose mid-region happens to be
// `[lime, teal, blue]` — so 2-element charts read as green+blue, and
// 4-element charts add #8AC926 (acidic lime) which clashes with the
// palette's blues. The colors are technically distinct, just not
// harmonious.
//
// Replace with a Tableau-style 11-color palette arranged so the mid
// distribution lands on classic well-balanced trios:
//
//   • n=2 → blue + red                        (high-contrast complementary)
//   • n=3 → blue + orange + red               (warm/cool balance)
//   • n=4 → blue + orange + red + green       (RYBG without lime acid)
//   • n=5 → yellow + blue + orange + red + green
//   • n=6..10 → expanding outward through teal / pink / purple / brown
//
// All hues are mid-saturation Tableau-derived shades — they read clearly
// on dark surfaces, don't fight each other, and look intentional rather
// than rainbow-default.
//
// Pass via the chart component's `customPalette` prop, which beats the
// `theme` prop in the lib's resolution order (customPalette || theme).
const MULTI_PALETTE: string[] = [
  '#9C755F', // 0: brown
  '#B07AA1', // 1: muted purple
  '#76B7B2', // 2: soft teal
  '#EDC948', // 3: warm yellow
  '#4E79A7', // 4: steel blue   (mid-1: shows for n=2)
  '#F28E2B', // 5: warm orange  (mid: shows for n=1, n=3 center, n=5 center)
  '#E15759', // 6: red           (mid+1: shows for n=2)
  '#59A14F', // 7: balanced green (joins at n=4)
  '#FF9DA7', // 8: rose pink
  '#BAB0AC', // 9: warm gray
  '#6F4D7E', // 10: deep plum
];

// Backwards-compatibility alias — slice charts (Pie / Donut / Radial /
// SingleStackedBar) used to use SLICE_PALETTE. The same palette now
// covers slice charts AND multi-series fallback to keep the visual
// language consistent across a single dashboard.
const SLICE_PALETTE = MULTI_PALETTE;
function effectivePalette(palette: PaletteName | undefined, seriesCount: number): PaletteName | undefined {
  if (!palette) return undefined;
  if (seriesCount > 1 && SINGLE_HUE_PALETTES.has(palette)) return 'vivid';
  return palette;
}
const themeArg = (palette?: PaletteName, seriesCount = 1): ChartArgs => {
  const eff = effectivePalette(palette, seriesCount);
  return eff ? { theme: eff } : {};
};

/**
 * Resolve the final palette props for a chart in three tiers:
 *   1. Caller (the LLM) provided customPalette → pass through verbatim.
 *      Maximum control: model can hand-pick brand colors, country-flag
 *      colors, mood colors, anything with at least 2 hex strings.
 *   2. Slice chart (pie / donut / radial / single-stacked-bar) with no
 *      explicit customPalette → use the curated MULTI_PALETTE. Beats
 *      upstream `theme="vivid"`'s lime+blue mid-distribution.
 *   3. Otherwise → resolve via `themeArg` (palette name → upstream
 *      theme, with single-hue→vivid fallback for multi-series).
 */
function pickPaletteProps(args: {
  palette?: PaletteName;
  customPalette?: string[];
  seriesCount: number;
  isSliceChart: boolean;
}): ChartArgs {
  if (args.customPalette && args.customPalette.length >= 2) {
    return { customPalette: args.customPalette };
  }
  if (args.isSliceChart) {
    return { customPalette: MULTI_PALETTE };
  }
  return themeArg(args.palette, args.seriesCount);
}

// ---- Helpers (inlined from @openuidev/react-ui/dist/genui-lib/helpers.js
// because that subpath isn't a public package export). Same logic, lifted
// directly from the lib so we stay parser-compatible.
function asArray(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  return [v];
}
function hasAllProps(obj: Record<string, unknown>, ...keys: string[]): boolean {
  return keys.every((k) => obj[k] != null);
}
type ElementNode = { type: 'element'; props: Record<string, unknown> };
function asElementNodes(v: unknown): ElementNode[] {
  return asArray(v).filter(
    (x): x is ElementNode =>
      typeof x === 'object' && x !== null && (x as { type?: unknown }).type === 'element',
  );
}
function buildChartData(labels: unknown, series: unknown): Record<string, string | number>[] {
  const lbls = asArray(labels) as string[];
  const rows = asArray(series);
  if (rows.length > 0 && Array.isArray(rows[0])) {
    const seriesNames = lbls.slice(1);
    return (rows as unknown[][]).map((row) => {
      const point: Record<string, string | number> = { category: String(row[0] ?? '') };
      seriesNames.forEach((name, si) => {
        const val = row[si + 1];
        point[name] = typeof val === 'number' ? val : Number(val) || 0;
      });
      return point;
    });
  }
  const seriesNodes = asElementNodes(series);
  return lbls.map((label, i) => {
    const point: Record<string, string | number> = { category: label };
    seriesNodes.forEach((s) => {
      const cat = s.props['category'];
      const vals = s.props['values'];
      if (typeof cat === 'string' && Array.isArray(vals) && i < vals.length) {
        const v = (vals as unknown[])[i];
        point[cat] = typeof v === 'number' ? v : Number(v) || 0;
      }
    });
    return point;
  });
}

// ---- Wrapped chart components

export const ThemedBarChart = defineComponent({
  name: 'BarChart',
  props: z.object({
    labels: z.array(z.string()),
    series: z.array(z.any()),
    variant: z.enum(['grouped', 'stacked']).optional(),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Vertical bars; use for comparing values across categories with one or more series. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'labels', 'series')) return null;
    const data = buildChartData(props.labels, props.series);
    if (!data.length) return null;
    return (
      <ChartGate label="bar chart" dataKey={JSON.stringify(data)}>
        <BarChartCondensedComponent
          data={data}
          categoryKey="category"
          variant={props.variant}
          xAxisLabel={props.xLabel}
          yAxisLabel={props.yLabel}
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: asArray(props.series).length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedLineChart = defineComponent({
  name: 'LineChart',
  props: z.object({
    labels: z.array(z.string()),
    series: z.array(z.any()),
    variant: z.enum(['linear', 'natural', 'step']).optional(),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Lines over categories; use for trends and continuous data over time. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'labels', 'series')) return null;
    const data = buildChartData(props.labels, props.series);
    if (!data.length) return null;
    return (
      <ChartGate label="line chart" dataKey={JSON.stringify(data)}>
        <LineChartCondensedComponent
          data={data}
          categoryKey="category"
          variant={props.variant}
          xAxisLabel={props.xLabel}
          yAxisLabel={props.yLabel}
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: asArray(props.series).length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedAreaChart = defineComponent({
  name: 'AreaChart',
  props: z.object({
    labels: z.array(z.string()),
    series: z.array(z.any()),
    variant: z.enum(['linear', 'natural', 'step']).optional(),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Filled area under lines; use for cumulative totals or volume trends over time. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'labels', 'series')) return null;
    const data = buildChartData(props.labels, props.series);
    if (!data.length) return null;
    return (
      <ChartGate label="area chart" dataKey={JSON.stringify(data)}>
        <AreaChartCondensedComponent
          data={data}
          categoryKey="category"
          variant={props.variant}
          xAxisLabel={props.xLabel}
          yAxisLabel={props.yLabel}
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: asArray(props.series).length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedRadarChart = defineComponent({
  name: 'RadarChart',
  props: z.object({
    labels: z.array(z.string()),
    series: z.array(z.any()),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Spider/web chart; use for comparing multiple variables across one or more entities. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'labels', 'series')) return null;
    const data = buildChartData(props.labels, props.series);
    if (!data.length) return null;
    return (
      <ChartGate label="radar chart" dataKey={JSON.stringify(data)}>
        <RadarChartComponent
          data={data}
          categoryKey="category"
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: asArray(props.series).length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedHorizontalBarChart = defineComponent({
  name: 'HorizontalBarChart',
  props: z.object({
    labels: z.array(z.string()),
    series: z.array(z.any()),
    variant: z.enum(['grouped', 'stacked']).optional(),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Horizontal bars; prefer when category labels are long or for ranked lists. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'labels', 'series')) return null;
    const data = buildChartData(props.labels, props.series);
    if (!data.length) return null;
    return (
      <ChartGate label="horizontal bar chart" dataKey={JSON.stringify(data)}>
        <HorizontalBarChartComponent
          data={data}
          categoryKey="category"
          variant={props.variant}
          xAxisLabel={props.xLabel}
          yAxisLabel={props.yLabel}
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: asArray(props.series).length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedPieChart = defineComponent({
  name: 'PieChart',
  props: z.object({
    labels: z.array(z.string()),
    values: z.array(z.number()),
    variant: z.enum(['pie', 'donut']).optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Circular slices; use plucked arrays: PieChart(data.categories, data.values). Pass customPalette: [hex, hex, ...] for exact slice colors (recommended for branded / themed pies). Otherwise leave palette unset and we use a balanced default.',
  component: ({ props }) => {
    const labels = asArray(props.labels) as string[];
    const values = asArray(props.values) as number[];
    if (!labels.length || !values.length) return null;
    const data = labels.map((cat, i) => ({
      category: cat,
      value: typeof values[i] === 'number' ? values[i] : 0,
    }));
    return (
      <ChartGate label="pie chart" dataKey={JSON.stringify(data)}>
        <PieChartComponent
          data={data}
          categoryKey="category"
          dataKey="value"
          variant={props.variant}
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: data.length,
            isSliceChart: true,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedRadialChart = defineComponent({
  name: 'RadialChart',
  props: z.object({
    labels: z.array(z.string()),
    values: z.array(z.number()),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Radial bars; use plucked arrays: RadialChart(data.categories, data.values). Pass customPalette: [hex, hex, ...] for exact ring colors. Otherwise we use a balanced default.',
  component: ({ props }) => {
    const labels = asArray(props.labels) as string[];
    const values = asArray(props.values) as number[];
    if (!labels.length || !values.length) return null;
    const data = labels.map((cat, i) => ({
      category: cat,
      value: typeof values[i] === 'number' ? values[i] : 0,
    }));
    return (
      <ChartGate label="radial chart" dataKey={JSON.stringify(data)}>
        <RadialChartComponent
          data={data}
          categoryKey="category"
          dataKey="value"
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: data.length,
            isSliceChart: true,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedSingleStackedBarChart = defineComponent({
  name: 'SingleStackedBarChart',
  props: z.object({
    labels: z.array(z.string()),
    values: z.array(z.number()),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'Single horizontal stacked bar; use plucked arrays: SingleStackedBarChart(data.categories, data.values). Pass customPalette: [hex, hex, ...] for exact segment colors. Otherwise we use a balanced default.',
  component: ({ props }) => {
    const labels = asArray(props.labels) as string[];
    const values = asArray(props.values) as number[];
    if (!labels.length || !values.length) return null;
    const data = labels.map((cat, i) => ({
      category: cat,
      value: typeof values[i] === 'number' ? values[i] : 0,
    }));
    return (
      <ChartGate label="stacked bar" dataKey={JSON.stringify(data)}>
        <SingleStackedBarComponent
          data={data}
          categoryKey="category"
          dataKey="value"
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: data.length,
            isSliceChart: true,
          })}
        />
      </ChartGate>
    );
  },
});

export const ThemedScatterChart = defineComponent({
  name: 'ScatterChart',
  props: z.object({
    datasets: z.array(z.any()),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    palette: PaletteEnum.optional(),
    customPalette: HexArrayArg,
  }),
  description:
    'X/Y scatter plot; use for correlations, distributions, and clustering. Pass palette for a preset, or customPalette: [hex, hex, ...] for exact dataset colors.',
  component: ({ props }) => {
    if (!hasAllProps(props as Record<string, unknown>, 'datasets')) return null;
    const unwrap = (node: unknown): Record<string, unknown> | null => {
      if (typeof node !== 'object' || node === null) return null;
      const n = node as { type?: string; props?: Record<string, unknown> };
      return n.type === 'element' ? n.props ?? {} : (node as Record<string, unknown>);
    };
    const rawDatasets = asArray(props.datasets);
    const data = rawDatasets
      .map((ds) => {
        const dsProps = unwrap(ds);
        if (!dsProps) return null;
        const rawPoints = asArray(dsProps['points']);
        return {
          name: String(dsProps['name'] ?? ''),
          data: rawPoints
            .map((pt) => {
              const ptProps = unwrap(pt);
              if (!ptProps) return null;
              const x = Number(ptProps['x']);
              const y = Number(ptProps['y']);
              const z = ptProps['z'] != null ? Number(ptProps['z']) : undefined;
              return z != null ? { x, y, z } : { x, y };
            })
            .filter((p): p is { x: number; y: number; z?: number } => p !== null),
        };
      })
      .filter((d): d is { name: string; data: { x: number; y: number; z?: number }[] } => d !== null);
    if (!data.length) return null;
    return (
      <ChartGate label="scatter chart" dataKey={JSON.stringify(data)}>
        <ScatterChartComponent
          data={data}
          xAxisDataKey="x"
          yAxisDataKey="y"
          isAnimationActive={false}
          {...pickPaletteProps({
            palette: props.palette,
            customPalette: props.customPalette,
            seriesCount: data.length,
            isSliceChart: false,
          })}
        />
      </ChartGate>
    );
  },
});

// Names of charts we replace, so the assembler in genui-dashboard-library
// knows which originals to drop.
export const REPLACED_CHART_NAMES = new Set([
  'BarChart',
  'LineChart',
  'AreaChart',
  'RadarChart',
  'HorizontalBarChart',
  'PieChart',
  'RadialChart',
  'SingleStackedBarChart',
  'ScatterChart',
]);

// Convenience export for the assembler. The cast is fine — each chart is
// a valid DefinedComponent<some schema>, but TS can't unify the per-chart
// generics into a single array type without help.
export const themedCharts = [
  ThemedBarChart,
  ThemedLineChart,
  ThemedAreaChart,
  ThemedRadarChart,
  ThemedHorizontalBarChart,
  ThemedPieChart,
  ThemedRadialChart,
  ThemedSingleStackedBarChart,
  ThemedScatterChart,
] as unknown as DefinedComponent[];
